import React, { useState, useEffect } from 'react';
import { Plus, Heart, Check, List, X, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { 
  getUserLists, 
  createList, 
  addWalkToList, 
  removeWalkFromList, 
  getWalkLists,
  isWalkInList 
} from '../data/mock';
import { useToast } from '../hooks/use-toast';

const AddToListDropdown = ({ walkId, onListChange }) => {
  const { toast } = useToast();
  const [lists, setLists] = useState({});
  const [walkLists, setWalkLists] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');

  useEffect(() => {
    refreshLists();
  }, [walkId]);

  const refreshLists = () => {
    const userLists = getUserLists();
    const currentWalkLists = getWalkLists(walkId);
    setLists(userLists);
    setWalkLists(currentWalkLists);
  };

  const handleToggleList = (listId) => {
    const isInList = isWalkInList(walkId, listId);
    
    if (isInList) {
      removeWalkFromList(walkId, listId);
      toast({
        title: "Removed from list",
        description: `Walk removed from "${lists[listId].name}"`,
      });
    } else {
      addWalkToList(walkId, listId);
      toast({
        title: "Added to list",
        description: `Walk added to "${lists[listId].name}"`,
      });
    }
    
    refreshLists();
    if (onListChange) onListChange();
  };

  const handleCreateList = () => {
    if (!newListName.trim()) return;
    
    try {
      createList(newListName.trim(), newListDescription.trim());
      setNewListName('');
      setNewListDescription('');
      setShowCreateForm(false);
      refreshLists();
      
      toast({
        title: "List created",
        description: `"${newListName}" list has been created`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const totalLists = Object.keys(lists).length;
  const walksInLists = walkLists.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="relative flex items-center gap-2"
        >
          <Heart className={`h-4 w-4 ${walksInLists > 0 ? 'fill-red-500 text-red-500' : ''}`} />
          Save
          {walksInLists > 0 && (
            <Badge variant="secondary" className="ml-1 h-4 text-xs">
              {walksInLists}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-64" align="end">
        <div className="px-2 py-1.5">
          <h4 className="font-semibold text-sm">Save to list</h4>
          <p className="text-xs text-gray-500">Organize your walks into custom lists</p>
        </div>
        
        <DropdownMenuSeparator />
        
        {Object.values(lists).map((list) => {
          const isInList = isWalkInList(walkId, list.id);
          return (
            <DropdownMenuItem 
              key={list.id}
              onClick={() => handleToggleList(list.id)}
              className="flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                  isInList ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                }`}>
                  {isInList && <Check className="h-3 w-3 text-white" />}
                </div>
                <div>
                  <span className="font-medium text-sm">{list.name}</span>
                  {list.description && (
                    <p className="text-xs text-gray-500">{list.description}</p>
                  )}
                </div>
              </div>
              <Badge variant="outline" className="text-xs">
                {list.walks.length}
              </Badge>
            </DropdownMenuItem>
          );
        })}
        
        <DropdownMenuSeparator />
        
        <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
          <DialogTrigger asChild>
            <DropdownMenuItem 
              onSelect={(e) => e.preventDefault()}
              className="cursor-pointer"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create new list
            </DropdownMenuItem>
          </DialogTrigger>
          
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New List</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="listName">List Name *</Label>
                <Input
                  id="listName"
                  placeholder="e.g., Morning Loops, Weekend Adventures"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  maxLength={50}
                />
              </div>
              
              <div>
                <Label htmlFor="listDescription">Description (optional)</Label>
                <Textarea
                  id="listDescription"
                  placeholder="Describe what kind of walks you'll save here..."
                  value={newListDescription}
                  onChange={(e) => setNewListDescription(e.target.value)}
                  rows={2}
                  maxLength={200}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateList}
                  disabled={!newListName.trim()}
                >
                  Create List
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddToListDropdown;